import { useLazyQuery, useMutation } from '@apollo/client';
import gql from 'graphql-tag';
import { enqueueSnackbar } from 'notistack';
import * as React from 'react';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  TextField,
  Typography,
} from '../../lib/base';
import type {
  EditRecurringPlantingNotesDataQuery,
  EditRecurringPlantingNotesDataQueryVariables,
  EditRecurringPlantingNotesMutation,
  EditRecurringPlantingNotesMutationVariables,
} from '../../types/__generated__/graphql';
import { createID } from '../../utils';
import { ExitDialogButton } from '../common/button';
import { ErrorView } from '../common/error';
import { PageLoading } from '../common/loading';

const EDIT_REC_PLANTING_NOTES_MUT = gql`
  mutation EditRecurringPlantingNotes(
    $recurring_planting_id: Int!
    $notes: String
    $modify_in_progress_plantings: Boolean!
  ) {
    editRecurringPlantingNotes(
      input: {
        recurring_planting_id: $recurring_planting_id
        notes: $notes
        modify_in_progress_plantings: $modify_in_progress_plantings
      }
    ) {
      updated_recurring_planting {
        id
        notes
        updated_at
      }
      updated_plantings {
        id
        planting_notes
        updated_at
      }
    }
  }
`;
const EDIT_REC_PLANTING_NOTES_DATA = gql`
  query EditRecurringPlantingNotesData($id: ID!) {
    recurring_planting(id: $id) {
      id
      notes
    }
  }
`;

interface Props {
  readonly recurringPlantingId: number;
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly onSuccess: (shouldRefetchSelf: boolean) => void;
}

export default function EditRecurringPlantingNotesDialog({ recurringPlantingId, isOpen, onClose, onSuccess }: Props) {
  const [editRecurringPlantingNotes, { loading: editSingleOrderLoading }] = useMutation<
    EditRecurringPlantingNotesMutation,
    EditRecurringPlantingNotesMutationVariables
  >(EDIT_REC_PLANTING_NOTES_MUT);
  const [getRecurringPlantingData, { loading: plantingDataLoading, error: plantingDataError }] = useLazyQuery<
    EditRecurringPlantingNotesDataQuery,
    EditRecurringPlantingNotesDataQueryVariables
  >(EDIT_REC_PLANTING_NOTES_DATA, {
    defaultOptions: {
      fetchPolicy: 'network-only',
    },
  });

  React.useEffect(() => {
    if (isOpen) {
      void getRecurringPlantingData({
        variables: { id: createID.recurringPlanting(recurringPlantingId) },
        onCompleted: (plantingDataIn) => {
          if (plantingDataIn?.recurring_planting != null) {
            setNotes(plantingDataIn.recurring_planting.notes ?? '');
            setExistingNotes(plantingDataIn.recurring_planting.notes ?? '');
          }
        },
        onError: (errorIn) => {
          enqueueSnackbar(errorIn.message, {
            variant: 'error',
            autoHideDuration: 10000,
          });
        },
      });
    }
  }, [isOpen, recurringPlantingId, getRecurringPlantingData]);

  const disableFields = editSingleOrderLoading || plantingDataLoading;

  const onCloseInternal = () => {
    onClose();
  };

  const onSubmit = (_e?: React.MouseEvent | React.TouchEvent) => {
    void editRecurringPlantingNotes({
      variables: {
        recurring_planting_id: recurringPlantingId,
        notes: notes === '' ? null : notes,
        modify_in_progress_plantings: shouldUpdateInProgressOrders,
      },
      onCompleted: (dataIn) => {
        if (dataIn !== undefined && dataIn.editRecurringPlantingNotes != null) {
          enqueueSnackbar('Updated recurring planting notes', { variant: 'success' });
          onSuccess(false);
        } else {
          enqueueSnackbar('Error editing planting notes', { variant: 'error', autoHideDuration: 10000 });
        }
      },
      onError: (errorIn) => {
        enqueueSnackbar(errorIn.message, { variant: 'error', autoHideDuration: 10000 });
      },
    });
  };

  const [notes, setNotes] = React.useState('');
  const [existingNotes, setExistingNotes] = React.useState('');
  const onChangeNotes = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setNotes(e.target.value);
  };
  const [shouldUpdateInProgressOrders, setShouldUpdateInProgressOrders] = React.useState(false);
  const onChangeShouldUpdateInProgressOrders = (e: React.ChangeEvent<HTMLInputElement>) => {
    setShouldUpdateInProgressOrders(e.target.checked);
  };

  const somethingChanged = existingNotes !== notes || shouldUpdateInProgressOrders;
  const isSubmitDisabled: boolean = disableFields || !somethingChanged || editSingleOrderLoading || plantingDataLoading;
  const onEnter = () => {
    if (!isSubmitDisabled && !disableFields) {
      onSubmit();
    }
  };

  const dialogContent = plantingDataError ? (
    <ErrorView error={plantingDataError} />
  ) : plantingDataLoading ? (
    <PageLoading />
  ) : (
    <Box display="flex" flexDirection="column" sx={{ m: 1 }} gap={2}>
      <TextField
        aria-describedby="planting-notes"
        id="planting-notes"
        label="Notes"
        value={notes}
        onChange={onChangeNotes}
        // onEnter={onEnter}
        disabled={disableFields}
      />
      <Box display="flex" flexDirection="row" flexWrap="nowrap" alignItems="center" justifyContent="flex-start">
        <FormControlLabel
          label="Update In Progress Child Plantings"
          control={<Checkbox checked={shouldUpdateInProgressOrders} onChange={onChangeShouldUpdateInProgressOrders} />}
        />
      </Box>
    </Box>
  );

  return (
    <Dialog
      open={isOpen}
      onClose={() => {
        onCloseInternal();
      }}
      maxWidth="md"
    >
      <DialogTitle>
        <Box display="flex" flexDirection="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Edit Recurring Planting Notes</Typography>
          <ExitDialogButton dontShiftUp onCloseDialog={onCloseInternal} />
        </Box>
      </DialogTitle>
      <DialogContent
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            onEnter();
          }
        }}
        sx={{ padding: 1 }}
      >
        {dialogContent}
      </DialogContent>
      <DialogActions disableSpacing sx={{ flexDirection: { md: 'row', xs: 'column' }, alignItems: 'center' }}>
        <Button
          sx={{ width: { xs: '100%', md: '50%' } }}
          variant="outlined"
          onClick={onCloseInternal}
          disabled={disableFields}
        >
          Cancel
        </Button>
        <Button
          id="submit-planting-button"
          sx={{ width: { xs: '100%', md: '50%' }, ml: { md: 1 }, mt: { xs: 1, md: 0 } }}
          variant="contained"
          onClick={onSubmit}
          type="submit"
          disabled={isSubmitDisabled}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              onEnter();
            }
          }}
        >
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
}
